import { fileObject } from "@boark/shared/types/file-object";
import { ProcurementOrganization } from "@boark/shared/types/procurement-organization";
import {
  faChevronDown,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useMemo, useState } from "react";

import { useGetFiles } from "../queries/get-files";
import { formatOrganizationLabel } from "../services/organization";
import { useLocale } from "../stores/locale";
import { formatPrice } from "../utils/format-price";
import { recordIdToId } from "../utils/record-id-to-id";
import { Button } from "./button";
import { EmbeddedFileList } from "./embedded-file-list";
import { FileUpload } from "./file-upload";
import { Radio } from "./radio";

function ProcurementOrganizationItem({
  procurementId,
  organization,
  readOnly,
  winnerSelection,
}: {
  procurementId: string;
  organization: ProcurementOrganization;
  readOnly: boolean;
  winnerSelection?: {
    selectedWinnerId: string;
    onSelectWinnerId: (organizationId: string) => void;
  };
}) {
  const { locale: t, currentLanguage } = useLocale();
  const organizationId = recordIdToId(organization.organization.id);
  const path = `procurement/${procurementId}/${organizationId}`;
  const { data: objects, invalidate } = useGetFiles(path);
  const [expanded, setExpanded] = useState(false);

  const files = useMemo(() => {
    return (objects ?? []).filter(
      (
        object,
      ): object is fileObject & {
        type: "file";
        size: number;
        lastModified: Date;
      } => object.type === "file",
    );
  }, [objects]);
  const hasFiles = files.length > 0;
  const isWinner =
    winnerSelection?.selectedWinnerId === organizationId;

  return (
    <li className="border border-gray-300 rounded-xs bg-white">
      <div className="flex flex-wrap items-center justify-between gap-2 p-3">
        <div className="flex min-w-0 items-start gap-3">
          {winnerSelection && (
            <label className="flex items-center gap-2 pt-0.5 cursor-pointer shrink-0">
              <Radio
                checked={isWinner}
                name="procurement-winner"
                value={organizationId}
                onChange={winnerSelection.onSelectWinnerId}
              />
            </label>
          )}
          <div className="min-w-0">
            <p className="font-medium text-gray-900 truncate">
              {formatOrganizationLabel(organization.organization)}
            </p>
            <p className="text-sm text-gray-500">
              {organization.price !== undefined
                ? formatPrice(organization.price, currentLanguage)
                : t.procurement.organizationPriceEmpty}
            </p>
          </div>
        </div>
        {!winnerSelection && (
          <div className="flex items-center gap-2 shrink-0">
            {hasFiles && (
              <Button
                severity="secondary"
                size="sm"
                onClick={() => setExpanded((value) => !value)}
              >
                <FontAwesomeIcon
                  className="mr-1"
                  icon={expanded ? faChevronDown : faChevronRight}
                />
                {t.procurement.organizationFilesCount.replace(
                  "{count}",
                  String(files.length),
                )}
              </Button>
            )}
            <FileUpload
              disabled={readOnly}
              path={path}
              onUpload={() => void invalidate()}
            />
          </div>
        )}
      </div>
      {!winnerSelection && hasFiles && expanded && (
        <EmbeddedFileList
          files={files}
          path={path}
          readOnly={readOnly}
          onChanged={() => void invalidate()}
        />
      )}
    </li>
  );
}

export function ProcurementOrganizationList({
  procurementId,
  organizations,
  readOnly = false,
  winnerSelection,
}: {
  procurementId: string;
  organizations: ProcurementOrganization[];
  readOnly?: boolean;
  winnerSelection?: {
    selectedWinnerId: string;
    onSelectWinnerId: (organizationId: string) => void;
  };
}) {
  const t = useLocale((state) => state.locale);

  if (organizations.length === 0) {
    return (
      <p className="text-sm text-gray-500">
        {t.procurement.organizationsEmpty}
      </p>
    );
  }

  return (
    <ul className="flex flex-col gap-2">
      {organizations.map((organization) => (
        <ProcurementOrganizationItem
          key={recordIdToId(organization.organization.id)}
          organization={organization}
          procurementId={procurementId}
          readOnly={readOnly}
          winnerSelection={winnerSelection}
        />
      ))}
    </ul>
  );
}
